/** WOODWORKING PROJECTS THAT SELL™ — CRAFT FAIR & CHRISTMAS EDITION — all customer-facing content and asset mapping. */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume {
  number: string
  title: string
  projects: string
  level: string
  levelName: string
  description: string
  cover: PlanImage
  projectList: string[]
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'rights'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Bonus {
  number: string
  title: string
  benefit: string
  value: string
  cover: PlanImage
}

export interface Faq {
  question: string
  answer: string
}

/** Image host: light WebP files in the store's Impultienda resources. */
export const IMAGE_ORIGIN = 'https://sb.impultienda.ar'
const IMG = `${IMAGE_ORIGIN}/storage/v1/object/public/store-resources/e7c683fc-8302-41b4-9241-36ca3076743a/`

export const PRODUCT = {
  name: 'WOODWORKING PROJECTS THAT SELL™',
  descriptor: '30 small woodworking projects to sell at craft fairs—or give as handmade Christmas gifts',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'BUILD & SELL RIGHTS INCLUDED',
  sentence: 'Build them as gifts—or sell every piece you make.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  'CRAFT FAIR & CHRISTMAS EDITION',
  '30 PROJECTS THAT SELL',
  'CUT LISTS IN INCHES + MILLIMETERS',
  '7-DAY MONEY-BACK GUARANTEE',
  'BOARDS · DECOR · GIFTS · TOYS · PET',
  'BUILD & SELL RIGHTS',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Preview', href: '#project-preview' },
  { label: 'The Volumes', href: '#volumes' },
  { label: 'Inside the Plans', href: '#technical-proof' },
  { label: 'Bonuses', href: '#bonuses' },
  { label: 'FAQ', href: '#faq' },
]

const PAGE_SIZE = { width: 1280, height: 1600 }

export const SNAPSHOT: SnapshotItem[] = [
  { label: '30 Projects That Sell', icon: 'projects' },
  { label: '6 Volumes · 6 Categories', icon: 'volumes' },
  { label: 'Cut Lists in in + mm', icon: 'dimensions' },
  { label: 'Build & Sell Rights', icon: 'rights' },
]

export const PROJECT_NAME = 'Project P06 — Stacked 2x4 Tabletop Christmas Tree (Volume 02)'

export const PROJECT_PAGES: ProjectPage[] = [
  { src: `${IMG}1793725464770_p06-1-8175483d.webp`, caption: 'Finished Build & Size', alt: 'Volume 02, page 5: stacked 2x4 tabletop Christmas tree, 12 × 16.5 in, painted tiers on a mantle', ...PAGE_SIZE },
  { src: `${IMG}1793725469680_p06-2-182602ed.webp`, caption: 'Three-View Dimensioned Plan', alt: 'Volume 02, page 6: front, side and top views of the 2x4 Christmas tree with every tier dimensioned in inches and millimeters', ...PAGE_SIZE },
  { src: `${IMG}1793725475181_p06-3-46ec66ae.webp`, caption: 'Exploded View & Cut List', alt: 'Volume 02, page 7: exploded view and complete cut list of the 2x4 Christmas tree', ...PAGE_SIZE },
  { src: `${IMG}1793725480542_p06-6-77378415.webp`, caption: 'Build & Sell Card', alt: 'Volume 02, page 10: craft-fair build and sell card for the 2x4 Christmas tree', ...PAGE_SIZE },
]

export const PLAN_BENEFITS: string[] = ['Pick a project.', 'Cut a batch from one board.', 'Price it and sell it.']

export const TECH_PROOF: TechProof[] = [
  { src: `${IMG}1793725485687_proof-1-12d42e14.webp`, title: 'The fair best seller', project: 'Edge-Grain Cutting Board · Volume 01', description: 'Seven glued strips, one glue-up, four to six boards per batch.', alt: 'Volume 01: edge-grain cutting board three-view plan', ...PAGE_SIZE },
  { src: `${IMG}1793725491334_proof-2-e8b03b18.webp`, title: 'The gift for dad', project: 'Six-Pack Beer Caddy · Volume 03', description: 'Peaked ends, dowel handle and a bottle opener—from one 1x8.', alt: 'Volume 03: six-pack beer caddy three-view plan', ...PAGE_SIZE },
  { src: `${IMG}1793725497336_proof-3-a8372344.webp`, title: 'Under the tree', project: 'Wooden Pickup Truck · Volume 05', description: 'Chunky, safe, spinning wheels—six trucks from one 2x4.', alt: 'Volume 05: wooden toy pickup truck three-view plan', ...PAGE_SIZE },
  { src: `${IMG}1793725503468_proof-4-f353437c.webp`, title: 'Sells all year', project: 'Cedar Birdhouse · Volume 06', description: 'One cedar board, a cleaning door and a classic peaked roof.', alt: 'Volume 06: cedar birdhouse three-view plan', ...PAGE_SIZE },
]

export const COVER_VOLUME_01: PlanImage = { src: `${IMG}1793725424097_cover-v01-4d624e2b.webp`, alt: 'Volume 01 cover: Woodworking Projects That Sell™ — Kitchen Boards & Serving Pieces', ...PAGE_SIZE }

/** Product mockup used in the hero and the offer. */
export const HERO_MOCKUP: PlanImage = {
  src: `${IMG}1793725459372_hero-9b6a2974.webp`,
  alt: 'WOODWORKING PROJECTS THAT SELL™: the stacked 2x4 Christmas tree from Volume 02, the six volume covers and a "+5 free bonuses" seal',
  width: 1200,
  height: 1200,
}

export const BONUS_LINE = 'Plus 5 premium bonus resources, included free.'

const cover = (src: string, number: string, title: string): PlanImage => ({
  src,
  alt: `Volume ${number} cover: Woodworking Projects That Sell™ — ${title}`,
  ...PAGE_SIZE,
})

export const VOLUMES: Volume[] = [
  {
    number: '01', title: 'Kitchen Boards & Serving Pieces', projects: '5 PROJECTS', level: 'BEST SELLERS', levelName: 'Start here',
    description: 'The pieces every fair table sells: boards, trays and coasters.',
    projectList: ['Edge-Grain Cutting Board', 'Charcuterie Paddle Board', 'Serving Tray with Handles', 'Coaster Set with Holder', 'Wine Bottle & Glass Holder'],
    cover: COVER_VOLUME_01,
  },
  {
    number: '02', title: 'Christmas Decor That Sells', projects: '5 PROJECTS', level: 'CHRISTMAS', levelName: 'November–December',
    description: 'Trees, stars, countdowns, lanterns and porch signs.',
    projectList: ['Stacked 2x4 Tabletop Christmas Tree', 'Rustic Slat Wall Star', 'Advent Countdown Block Calendar', 'Lantern Candle Box', 'Tall "Merry Christmas" Porch Sign'],
    cover: cover(`${IMG}1793725429768_cover-v02-f5d51587.webp`, '02', 'Christmas Decor That Sells'),
  },
  {
    number: '03', title: 'Gift Boxes & Caddies', projects: '5 PROJECTS', level: 'GIFTS', levelName: 'For him & for her',
    description: 'Keepsake box, beer caddy, wine box, valet tray and recipe box.',
    projectList: ['Hardwood Keepsake Box', 'Six-Pack Beer Caddy', 'Single Wine Bottle Gift Box', 'Valet Catch-All Tray', 'Recipe Card Box'],
    cover: cover(`${IMG}1793725436441_cover-v03-b7642285.webp`, '03', 'Gift Boxes & Caddies'),
  },
  {
    number: '04', title: 'Shelves & Wall Pieces', projects: '5 PROJECTS', level: 'HOME', levelName: 'Steady all year',
    description: 'Floating shelf, key rack, spice rack, rope plant shelf and coat rack.',
    projectList: ['Floating 2x6 Shelf', 'Entryway Key Rack with Shelf', 'Three-Tier Spice Rack', 'Hanging Rope Plant Shelf', 'Peg Coat Rack'],
    cover: cover(`${IMG}1793725442158_cover-v04-c49c4a91.webp`, '04', 'Shelves & Wall Pieces'),
  },
  {
    number: '05', title: 'Wooden Toys & Kids Gifts', projects: '5 PROJECTS', level: 'KIDS', levelName: 'Grandparent favorites',
    description: 'Truck, airplane, tic-tac-toe, tumbling tower and step stool.',
    projectList: ['Wooden Pickup Truck', 'Tic-Tac-Toe Board Game', 'Kids Step Stool', 'Tumbling Tower Block Game', 'Wooden Toy Airplane'],
    cover: cover(`${IMG}1793725447742_cover-v05-e0414d90.webp`, '05', 'Wooden Toys & Kids Gifts'),
  },
  {
    number: '06', title: 'Pet, Garden & Porch Projects', projects: '5 PROJECTS', level: 'PETS & GARDEN', levelName: 'Personalized sellers',
    description: 'Dog bowl stand, toy box, birdhouse, feeder and planter.',
    projectList: ['Raised Dog Bowl Stand', 'Dog Toy Box', 'Cedar Birdhouse', 'Hanging Platform Bird Feeder', 'Cedar Planter Box'],
    cover: cover(`${IMG}1793725454021_cover-v06-b5b056a8.webp`, '06', 'Pet, Garden & Porch Projects'),
  },
]

const BONUS_COVER_SIZE = { width: 1024, height: 1024 }

const bonus = (number: string, title: string, benefit: string, value: string, src: string): Bonus => ({
  number, title, benefit, value,
  cover: { src, alt: `Bonus ${number}: ${title} — Woodworking Projects That Sell™`, ...BONUS_COVER_SIZE },
})

export const BONUSES: Bonus[] = [
  bonus('01', 'Craft Fair Pricing & Profit Kit', 'Price every piece from real costs—materials, hours and booth fee—before the fair.', 'US$19', `${IMG}1793725398315_bonus-01-197bad7e.webp`),
  bonus('02', 'Holiday Stock & Batch Build Planner', 'Plan how many of each piece to build and batch them by board and setup.', 'US$15', `${IMG}1793725402973_bonus-02-5db53c54.webp`),
  bonus('03', 'Booth Display & Fair Day Checklist', 'Set up a table that sells: layout, signs, packing list and fair-day routine.', 'US$12', `${IMG}1793725407733_bonus-03-e4589f01.webp`),
  bonus('04', 'Printable Price Tags, Gift Tags & Care Cards', 'Ready-to-print tags and care cards that make every piece look premium.', 'US$12', `${IMG}1793725412151_bonus-04-a993eeae.webp`),
  bonus('05', 'Food-Safe, Toy-Safe & Outdoor Finishing Guide', 'The right finish for boards, toys and outdoor pieces—every time.', 'US$15', `${IMG}1793725416971_bonus-05-df04b0c4.webp`),
]

export const BONUS_TOTAL = 'US$73'

export const OFFER_FEATURES: string[] = [
  '30 woodworking projects that sell in 6 premium PDF volumes (240 pages)',
  'Christmas decor, gifts, kitchen boards, toys, shelves and pet pieces',
  'Small builds from standard lumber—batch them in a weekend',
  '3-view plans and exploded views with every dimension',
  'Complete cut lists in inches and millimeters',
  'Step-by-step builds and joinery details',
  'A build & sell card for every project and a pricing worksheet in every volume',
  'Free updates, lifetime access & personal email support',
  'Build & sell rights for every piece you make',
  '5 premium bonus resources included free',
]

export const FAQS: Faq[] = [
  { question: 'What exactly am I buying?', answer: 'A digital collection of 30 woodworking projects in six PDF volumes. Each project has a finished view, a 3-view dimensioned plan, an exploded view with a complete cut list in inches and millimeters, step-by-step build, joinery details and a build & sell card.' },
  { question: 'Is this a monthly membership?', answer: 'No. You pay once and keep all 30 projects and 5 bonuses forever, with free updates.' },
  { question: 'I am a beginner. Can I build these?', answer: 'Yes. These are small projects made from standard boards with basic tools. Start with the cutting board, the 2x4 Christmas tree or the coaster set—each one takes an afternoon.' },
  { question: 'What tools do I need?', answer: 'A miter saw, a circular or table saw, a drill/driver, a jigsaw, a random orbit sander, clamps and wood glue. A router is optional for rounded edges.' },
  { question: 'Can I sell what I build?', answer: 'Yes. You may build and sell every physical piece—at craft fairs, markets, Etsy or Facebook Marketplace. The digital plans and illustrations may not be resold, shared or redistributed.' },
  { question: 'Do you tell me how much to charge?', answer: 'Every project has a build & sell card and every volume has a pricing worksheet: you add your real material cost, hours and booth fee and get your price. We never promise earnings—your local market sets the price.' },
  { question: 'How will I receive my files?', answer: 'Right after payment you receive an email with access to all six PDF volumes and the five bonus resources. Your access never expires.' },
  { question: 'What if it is not for me?', answer: 'You are covered by our 7-day money-back guarantee. Email us within 7 days and we refund 100% of your payment—no questions asked.' },
  { question: 'What if I need help?', answer: 'Write to soporte@creativodigital.shop and our team will answer you personally.' },
  { question: 'Are these physical books or kits?', answer: 'You are buying digital PDF plans. Lumber, hardware, tools and printed books are not included.' },
]

export const SUPPORT_EMAIL = 'soporte@creativodigital.shop'

export const GUARANTEE_LINE = '7-day money-back guarantee · 100% refund, no questions asked.'

/** Audience chips under the hero, so each buyer recognizes themselves. */
export const AUDIENCE: string[] = ['Craft fair sellers', 'Etsy & Marketplace makers', 'Garage-shop hobbyists', 'Christmas gift makers', 'Grandparents', 'Beginners']

export const NUMBERS: Array<{ value: string; label: string }> = [
  { value: '6', label: 'volumes' },
  { value: '30', label: 'projects' },
  { value: '240', label: 'pages' },
  { value: '5', label: 'free bonuses' },
]

export const PAINS: string[] = [
  'You build for a week—and barely sell enough to cover the costs.',
  'You never know what will actually sell, so the table goes home full.',
  'You don’t have time to build stock between orders and the season.',
  'Free plans skip the one step that matters—and good lumber becomes scrap.',
  'You have no idea what to charge, so you either scare buyers or work for free.',
]

export const COSTS: Array<{ title: string; text: string }> = [
  { title: 'A membership', text: 'Plan memberships charge you every single month—and the projects stop when you cancel.' },
  { title: 'Wasted lumber', text: 'One ruined glue-up of hardwood costs more than this whole collection.' },
  { title: 'Store-bought gifts', text: 'Buying Christmas gifts for the whole family, when you could build them.' },
  { title: 'A missed season', text: 'Christmas is the biggest selling season for wood crafts. It comes once a year.' },
]

export const WHY_TODAY: string[] = [
  'Christmas season: the fairs and the gift orders are now.',
  'Launch price: 64% off the 2026 Edition, for a limited time.',
  'Start this weekend: the cutting board and the 2x4 tree take an afternoon.',
]
